import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardSubtitle,
  IonContent,
  IonDatetime,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonLoading,
  IonNote,
  IonPage,
  IonSelect,
  IonSelectOption,
  IonText,
  IonTextarea,
  useIonToast,
} from "@ionic/react";
import { attachOutline, closeCircleOutline, timeOutline } from "ionicons/icons";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import Header from "src/components/Header";
import {
  DptoHabilitado,
  Motivo,
  crearCita,
  listarDptos,
  listarMotivos,
  listarSlotsDisponibles,
  subirAdjunto,
} from "src/services/atencionCliente";

const AtcNuevaCita: React.FC = () => {
  const history = useHistory();
  const [present] = useIonToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [cargando, setCargando] = useState(false);
  const [enviando, setEnviando] = useState(false);

  const [motivos, setMotivos] = useState<Motivo[]>([]);
  const [dptos, setDptos] = useState<DptoHabilitado[]>([]);

  const [dptoId, setDptoId] = useState<number | null>(null);
  const [motivoId, setMotivoId] = useState<number | null>(null);
  const [fechaSeleccion, setFechaSeleccion] = useState<string>("");
  const [slots, setSlots] = useState<string[]>([]);
  const [cargandoSlots, setCargandoSlots] = useState(false);
  const [horarioElegido, setHorarioElegido] = useState<string>("");
  const [observaciones, setObservaciones] = useState<string>("");
  const [archivos, setArchivos] = useState<File[]>([]);

  useEffect(() => {
    (async () => {
      setCargando(true);
      try {
        const [ms, ds] = await Promise.all([listarMotivos(), listarDptos()]);
        setMotivos(ms);
        setDptos(ds);
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Error al cargar catalogos";
        present({ message: msg, duration: 3000, color: "danger" });
      } finally {
        setCargando(false);
      }
    })();
  }, [present]);

  // Motivos visibles para el DPTO actual:
  //   - los que tienen dpto_sugerido_id == dptoId (especificos del DPTO)
  //   - los que tienen dpto_sugerido_id == null (globales tipo "Otros")
  const motivosFiltrados = useMemo<Motivo[]>(() => {
    if (dptoId === null) return [];
    return motivos.filter(
      (m) => m.dpto_sugerido_id === dptoId || m.dpto_sugerido_id === null,
    );
  }, [motivos, dptoId]);

  // Si cambio el DPTO, reseteo motivo y horario.
  const elegirDpto = (id: number) => {
    setDptoId(id);
    setMotivoId(null);
    setHorarioElegido("");
  };

  const cargarSlots = useCallback(
    async (dpto: number, fechaIso: string) => {
      const fechaSolo = fechaIso.slice(0, 10);
      setCargandoSlots(true);
      try {
        const lista = await listarSlotsDisponibles(dpto, fechaSolo);
        setSlots(lista);
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Error al consultar slots";
        present({ message: msg, duration: 3000, color: "danger" });
        setSlots([]);
      } finally {
        setCargandoSlots(false);
      }
    },
    [present],
  );

  useEffect(() => {
    if (dptoId && fechaSeleccion) {
      void cargarSlots(dptoId, fechaSeleccion);
    } else {
      setSlots([]);
    }
    setHorarioElegido("");
  }, [dptoId, fechaSeleccion, cargarSlots]);

  const onSubirArchivos = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const lista = Array.from(e.target.files).slice(0, 5);
    setArchivos(lista);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const quitarArchivo = (idx: number) => {
    setArchivos((prev) => prev.filter((_, i) => i !== idx));
  };

  const puedeConfirmar = (): boolean =>
    dptoId !== null &&
    motivoId !== null &&
    horarioElegido !== "" &&
    observaciones.trim() !== "" &&
    !enviando;

  const confirmar = async () => {
    if (!dptoId || !motivoId || !horarioElegido) {
      present({
        message: "Completa departamento, motivo y horario.",
        duration: 2500,
        color: "warning",
      });
      return;
    }
    if (!observaciones.trim()) {
      present({
        message: "Las observaciones son obligatorias.",
        duration: 2500,
        color: "warning",
      });
      return;
    }
    setEnviando(true);
    try {
      const cita = await crearCita({
        motivo_id: motivoId,
        dpto_destino_id: dptoId,
        fecha_hora_agendada: horarioElegido,
        observaciones: observaciones.trim(),
      });
      for (const f of archivos) {
        try {
          await subirAdjunto(cita.id, f);
        } catch (err) {
          const msg = err instanceof Error ? err.message : "Error subiendo archivo";
          present({ message: `${f.name}: ${msg}`, duration: 3000, color: "warning" });
        }
      }
      present({ message: "Cita agendada", duration: 2500, color: "success" });
      history.replace(`/atencion-cliente/citas/${cita.id}`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "No se pudo agendar";
      present({ message: msg, duration: 3500, color: "danger" });
    } finally {
      setEnviando(false);
    }
  };

  const motivoSeleccionado = motivos.find((m) => m.id === motivoId);
  const dptoSeleccionado = dptos.find((d) => d.grupo_id === dptoId);

  return (
    <IonPage>
      <Header titulo="Nueva cita" />
      <IonContent>
        <IonLoading
          isOpen={cargando || enviando}
          message={enviando ? "Agendando..." : "Cargando..."}
        />

        {/* Bloque 1: datos basicos */}
        <IonCard>
          <IonCardHeader>
            <IonCardSubtitle>Datos de la cita</IonCardSubtitle>
          </IonCardHeader>
          <IonList lines="full">
            <IonItem>
              <IonLabel position="stacked">Departamento destino *</IonLabel>
              <IonSelect
                value={dptoId}
                onIonChange={(e) => elegirDpto(Number(e.detail.value))}
                placeholder="Selecciona el departamento"
                interface="action-sheet"
              >
                {dptos.map((d) => (
                  <IonSelectOption key={d.grupo_id} value={d.grupo_id}>
                    {d.nombre_visible}
                  </IonSelectOption>
                ))}
              </IonSelect>
            </IonItem>

            <IonItem>
              <IonLabel position="stacked">Motivo *</IonLabel>
              <IonSelect
                value={motivoId}
                onIonChange={(e) => setMotivoId(Number(e.detail.value))}
                placeholder={
                  dptoId === null ? "Elegi primero un departamento" : "Selecciona el motivo"
                }
                interface="action-sheet"
                disabled={dptoId === null}
              >
                {motivosFiltrados.map((m) => (
                  <IonSelectOption key={m.id} value={m.id}>
                    {m.nombre}
                  </IonSelectOption>
                ))}
              </IonSelect>
              {dptoId !== null && motivosFiltrados.length === 0 && (
                <IonNote slot="helper" color="warning">
                  Este departamento no tiene motivos cargados.
                </IonNote>
              )}
            </IonItem>

            <IonItem lines="none">
              <IonLabel position="stacked">Fecha *</IonLabel>
              <IonDatetime
                displayFormat="DD/MM/YYYY"
                pickerFormat="DD/MM/YYYY"
                cancelText="Cancelar"
                doneText="Listo"
                value={fechaSeleccion}
                placeholder="Selecciona la fecha"
                onIonChange={(e) => setFechaSeleccion(String(e.detail.value || ""))}
              />
            </IonItem>
          </IonList>
        </IonCard>

        {/* Bloque 2: slot picker */}
        <IonCard>
          <IonCardHeader>
            <IonCardSubtitle>
              <IonIcon icon={timeOutline} style={{ verticalAlign: "middle", marginRight: 6 }} />
              Horario disponible
            </IonCardSubtitle>
          </IonCardHeader>
          <IonCardContent>
            {!dptoId || !fechaSeleccion ? (
              <IonText color="medium">
                <p style={{ margin: 0 }}>
                  Elegi un <strong>departamento</strong> y una <strong>fecha</strong> para ver los
                  horarios disponibles.
                </p>
              </IonText>
            ) : cargandoSlots ? (
              <IonText color="medium">
                <p style={{ margin: 0 }}>Buscando horarios...</p>
              </IonText>
            ) : slots.length === 0 ? (
              <IonText color="medium">
                <p style={{ margin: 0 }}>Sin horarios disponibles para esta fecha.</p>
              </IonText>
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: "0.5rem",
                }}
              >
                {slots.map((s) => {
                  const seleccionado = horarioElegido === s;
                  const hh = new Date(s).toLocaleTimeString("es-PY", {
                    hour: "2-digit",
                    minute: "2-digit",
                  });
                  return (
                    <IonButton
                      key={s}
                      size="default"
                      fill={seleccionado ? "solid" : "outline"}
                      color={seleccionado ? "primary" : "medium"}
                      onClick={() => setHorarioElegido(s)}
                    >
                      {hh}
                    </IonButton>
                  );
                })}
              </div>
            )}
          </IonCardContent>
        </IonCard>

        {/* Bloque 3: observaciones (obligatorio) + adjuntos (opcional) */}
        <IonCard>
          <IonCardHeader>
            <IonCardSubtitle>Detalles</IonCardSubtitle>
          </IonCardHeader>
          <IonList lines="full">
            <IonItem>
              <IonLabel position="stacked">Observaciones *</IonLabel>
              <IonTextarea
                value={observaciones}
                onIonChange={(e) => setObservaciones(e.detail.value || "")}
                rows={4}
                placeholder="Contanos brevemente por que necesitas la cita."
                autoGrow
              />
              <IonNote slot="helper" color={observaciones.trim() ? "medium" : "danger"}>
                Las observaciones son obligatorias para que el responsable pueda atenderte mejor.
              </IonNote>
            </IonItem>

            <IonItem lines="none">
              <IonLabel position="stacked">Adjuntos (max 5, 10 MB c/u)</IonLabel>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx"
                onChange={onSubirArchivos}
                style={{ display: "none" }}
              />
              <IonButton
                fill="outline"
                expand="block"
                onClick={() => fileInputRef.current?.click()}
                style={{ marginTop: "0.5rem" }}
              >
                <IonIcon icon={attachOutline} slot="start" />
                Elegir archivos
              </IonButton>
              {archivos.length > 0 && (
                <ul
                  style={{
                    paddingInlineStart: 0,
                    listStyle: "none",
                    marginTop: "0.75rem",
                    width: "100%",
                  }}
                >
                  {archivos.map((f, i) => (
                    <li
                      key={i}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "0.25rem 0",
                        fontSize: "0.85em",
                      }}
                    >
                      <span
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {f.name} ({(f.size / 1024).toFixed(0)} KB)
                      </span>
                      <IonIcon
                        icon={closeCircleOutline}
                        color="danger"
                        style={{ cursor: "pointer", fontSize: "1.25em", flexShrink: 0 }}
                        onClick={() => quitarArchivo(i)}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </IonItem>
          </IonList>
        </IonCard>

        {/* Resumen */}
        {dptoSeleccionado && motivoSeleccionado && horarioElegido && (
          <IonCard color="light">
            <IonCardContent>
              <IonText>
                <p style={{ margin: 0, fontSize: "0.9em" }}>
                  Vas a agendar <strong>{motivoSeleccionado.nombre}</strong> en{" "}
                  <strong>{dptoSeleccionado.nombre_visible}</strong> el{" "}
                  <strong>{new Date(horarioElegido).toLocaleString("es-PY")}</strong>.
                </p>
              </IonText>
            </IonCardContent>
          </IonCard>
        )}

        <div style={{ padding: "1rem", paddingBottom: "2rem" }}>
          <IonButton
            expand="block"
            color="success"
            disabled={!puedeConfirmar()}
            onClick={() => void confirmar()}
            style={{ marginBottom: "0.5rem" }}
          >
            Agendar cita
          </IonButton>
          <IonButton
            expand="block"
            fill="clear"
            color="medium"
            onClick={() => history.goBack()}
          >
            Cancelar
          </IonButton>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default AtcNuevaCita;
